/** 「回合时间线」的文案：列表页、详情页、时间轴主图。 */

export const en = {
  title: 'Turn timelines',
  modes: { say: 'Say', act: 'Act' },
  sources: { client: 'Frontend', server: 'Backend', model: 'Model', tts: 'TTS' },
  metrics: {
    first_feedback: 'First visible feedback',
    npc_first_text: 'NPC first text',
    npc_first_sound: 'NPC first sound',
    input_unlocked: 'Input unlocked',
    longest_gap: 'Longest idle gap',
    idle_total: 'Total idle time',
    gapCount: '{count} gaps',
    missing: 'No such node in this turn',
  },
  list: {
    window: 'Time range',
    windows: { day: 'Last 24 hours', week: 'Last 7 days', month: 'Last 30 days' },
    turnId: 'Turn ID',
    turnIdPlaceholder: 'Paste a turn_id to open its timeline',
    openTurn: 'Open',
    refresh: 'Refresh',
    summaryTitle: 'Median of the range',
    summaryHint: 'p90 {p90} · {turns} turns',
    summaryEmpty: 'No turn with this node yet',
    turnsTitle: 'Turns',
    emptyTitle: 'No timelines in this range',
    emptyDescription: 'Timelines are recorded for turns played after the feature shipped.',
    columns: { time: 'Started', turn: 'Turn', spread: 'Where the time went' },
    spread: {
      kp: 'KP result at',
      npc: 'NPC result at',
      rest: 'Input unlocked at',
      noClient: 'No frontend timeline; showing backend moments only',
    },
    pageSummary: '{total} turns · page {page} / {pages}',
    previous: 'Previous',
    next: 'Next',
    pageSize: 'Per page',
  },
  detail: {
    title: 'Turn timeline',
    back: 'Back to list',
    turnNo: 'Turn {no}',
    chartTitle: 'Time axis',
    aligned: 'Backend and model times are shifted by +{ms} ms to the frontend clock, measured on {event} (earliest receive minus emit). All times count from the moment the player submitted.',
    noClient: 'The frontend has not reported this turn. Times count from the moment the backend received the turn.',
    unaligned: 'The frontend and backend timelines share no event, so they could not be aligned. Backend and model times are shown without any shift.',
    notRecordedTitle: 'No timeline for this turn',
    notRecordedDescription: 'This turn was played before timelines were recorded.',
  },
  spans: {
    title: 'Longest stretches',
    share: '{percent}% of the turn',
    idle: 'Idle',
  },
  nodes: {
    title: 'All nodes',
    at: 'At',
    source: 'Source',
    name: 'Node',
    gap: 'Since previous',
  },
  chart: {
    ariaLabel: 'Turn timeline chart',
    zoom: { fit: 'Fit', x2: '2×', x4: '4×' },
    legend: {
      wait: 'Waiting for first token',
      generate: 'Generating',
      video: 'Video playing',
      idle: 'Idle gap: nothing playing, nothing new',
      metric: 'Key moment',
    },
    gap: 'idle {seconds}',
    clustered: '{text} +{count}',
    note: "Numbers with {'@'} are moments counted from the player's submit; numbers without {'@'} are durations. Only key moments are drawn; every node is in the table below. Hover a dot or bar for exact times.",
    lanes: {
      screen: 'Player sees',
      sound: 'Player hears',
      events: 'Backend events',
      parse: 'Output parsing',
      model: 'Model calls',
      tts: 'TTS synthesis',
    },
    metrics: {
      first_feedback: 'First feedback',
      npc_first_text: 'NPC text',
      npc_first_sound: 'NPC sound',
      input_unlocked: 'Unlocked',
    },
    roles: {
      kp: 'KP',
      npc: 'NPC',
      npc_action_kp: 'NPC action KP',
      player_talking_video_selection: 'Player video pick',
      intent_video_selection: 'Action video match',
      story_branch_designer: 'Branch designer',
      generated_ending: 'Generated ending',
    },
    model: {
      wait: 'first token {seconds}',
      generate: 'generate {seconds}',
      tokens: '{tokens} tok',
    },
    tts: { npc: 'TTS NPC', player: 'TTS player' },
    audio: {
      playing: { npc: 'NPC voice', player: 'Player voice' },
      waiting: { npc: 'Waiting for NPC audio', player: 'Waiting for player audio' },
    },
    events: {
      player_kp: 'KP judgement',
      speech_player: 'player voice done',
      speech_chunk_player: 'player voice first chunk',
      speech_npc: 'NPC voice done',
      speech_chunk_npc: 'NPC voice first chunk',
      dynamic_story: 'dynamic story',
      npc_response: 'NPC response',
      npc_action_kp: 'NPC action judgement',
      final: 'final',
      turn_playback_plan: 'playback plan',
      turn_playback_phase_player: 'player playback phase',
      turn_playback_phase_npc: 'NPC playback phase',
      action_rejected: 'action rejected',
      error: 'error',
    },
    server: {
      emitted: 'Sent {event}',
      queued: '{event} waits for the frontend',
    },
    parse: {
      kp_core: 'fields the NPC needs parsed, NPC call started',
      kp_rest: 'remaining KP fields (dynamic story)',
      expression_state: 'expression state parsed',
      narration: 'narration parsed',
      npcLine: 'line parsed',
      rest: 'remaining fields (extra wait without streaming)',
    },
    client: {
      textStream: 'NPC text',
      stateCg: 'State CG',
      video: {
        speech: { player: 'Player talking video', npc: 'NPC talking video' },
        action: { player: 'Action video', npc: 'Action video' },
      },
      marks: {
        submit: 'Submit',
        input_unlocked: 'Input unlocked',
        text_kp: 'KP narration shown',
        text_npc: 'NPC text shown',
        text_npc_action: 'NPC action narration shown',
        text_deferred: 'Held output released',
        options_shown: 'Suggestion buttons shown',
        stat_flash: 'Stat sound and glow',
        clue_toast: 'New clue toast',
        status_kp_done: 'Status: KP done',
      },
    },
  },
}

export const zh = {
  title: '回合时间线',
  modes: { say: '说', act: '做' },
  sources: { client: '前端', server: '后端', model: '模型', tts: 'TTS' },
  metrics: {
    first_feedback: '第一个可见反馈',
    npc_first_text: 'NPC 第一个字',
    npc_first_sound: 'NPC 第一声',
    input_unlocked: '输入解锁',
    longest_gap: '最长空档',
    idle_total: '空档合计',
    gapCount: '共 {count} 段空档',
    missing: '这个回合没有对应节点',
  },
  list: {
    window: '时间范围',
    windows: { day: '近 24 小时', week: '近 7 天', month: '近 30 天' },
    turnId: '回合 ID',
    turnIdPlaceholder: '粘贴 turn_id，直接打开它的时间线',
    openTurn: '查看',
    refresh: '刷新',
    summaryTitle: '区间中位数',
    summaryHint: 'p90 {p90} · {turns} 个回合',
    summaryEmpty: '还没有带这个节点的回合',
    turnsTitle: '回合',
    emptyTitle: '这个时间范围内没有时间线',
    emptyDescription: '时间线从功能上线之后玩的回合开始记录。',
    columns: { time: '开始时间', turn: '回合', spread: '时间花在哪' },
    spread: {
      kp: 'KP 出结果于',
      npc: 'NPC 出结果于',
      rest: '输入解锁于',
      noClient: '没有前端时间线，只显示后端时刻',
    },
    pageSummary: '共 {total} 个回合 · 第 {page} / {pages} 页',
    previous: '上一页',
    next: '下一页',
    pageSize: '每页',
  },
  detail: {
    title: '回合时间线',
    back: '返回列表',
    turnNo: '第 {no} 回合',
    chartTitle: '时间轴',
    aligned: '后端和模型的时刻统一加了 +{ms} ms 对齐到前端时钟，偏移量取自 {event}（前端收到时刻 − 后端发出时刻的最小值）。所有时刻从玩家点提交算起。',
    noClient: '前端还没有上报这个回合。所有时刻从后端收到回合算起。',
    unaligned: '前端和后端的时间线没有共同的事件，没法对齐。后端和模型的时刻按原值显示，没有加偏移。',
    notRecordedTitle: '这个回合没有时间线',
    notRecordedDescription: '它是在时间线开始记录之前玩的。',
  },
  spans: {
    title: '耗时最长的几段',
    share: '占整个回合 {percent}%',
    idle: '空档',
  },
  nodes: {
    title: '全部节点',
    at: '时刻',
    source: '来源',
    name: '节点',
    gap: '距上一节点',
  },
  chart: {
    ariaLabel: '回合时间轴',
    zoom: { fit: '适应宽度', x2: '2 倍', x4: '4 倍' },
    legend: {
      wait: '等首 token',
      generate: '生成中',
      video: '视频播放',
      idle: '空档：没有东西在播，也没有新东西出现',
      metric: '关键时刻',
    },
    gap: '空档 {seconds}',
    clustered: '{text} +{count}',
    note: "带 {'@'} 的数字是从玩家点提交算起的时刻，不带 {'@'} 的是时长。图上只画关键节点，全部节点在下面的明细表里。鼠标停在点或色块上能看到精确时刻。",
    lanes: {
      screen: '玩家看到的',
      sound: '玩家听到的',
      events: '后端事件',
      parse: '模型输出解析',
      model: '模型调用',
      tts: 'TTS 合成',
    },
    metrics: {
      first_feedback: '第一个反馈',
      npc_first_text: 'NPC 出字',
      npc_first_sound: 'NPC 出声',
      input_unlocked: '解锁',
    },
    roles: {
      kp: 'KP',
      npc: 'NPC',
      npc_action_kp: 'NPC 动作 KP',
      player_talking_video_selection: '玩家选片',
      intent_video_selection: '动作视频匹配',
      story_branch_designer: '分支设计',
      generated_ending: '结局生成',
    },
    model: {
      wait: '等首 token {seconds}',
      generate: '生成 {seconds}',
      tokens: '{tokens} token',
    },
    tts: { npc: 'TTS NPC', player: 'TTS 玩家' },
    audio: {
      playing: { npc: 'NPC 配音', player: '玩家配音' },
      waiting: { npc: '等 NPC 配音首片', player: '等玩家配音首片' },
    },
    events: {
      player_kp: 'KP 判定',
      speech_player: '玩家配音收尾',
      speech_chunk_player: '玩家配音首片',
      speech_npc: 'NPC 配音收尾',
      speech_chunk_npc: 'NPC 配音首片',
      dynamic_story: '动态剧情',
      npc_response: 'NPC 回应',
      npc_action_kp: 'NPC 动作判定',
      final: 'final',
      turn_playback_plan: '播放计划',
      turn_playback_phase_player: '玩家播放阶段',
      turn_playback_phase_npc: 'NPC 播放阶段',
      action_rejected: '动作被拒',
      error: '报错',
    },
    server: {
      emitted: '发出 {event}',
      queued: '{event} 等前端处理',
    },
    parse: {
      kp_core: '解析出 NPC 所需字段，NPC 已叫起',
      kp_rest: 'KP 其余字段生成中（动态剧情）',
      expression_state: '解析出表演状态',
      narration: '解析旁白',
      npcLine: '解析台词',
      rest: '其余字段生成中（不流式要多等这段）',
    },
    client: {
      textStream: 'NPC 出字',
      stateCg: '状态 CG',
      video: {
        speech: { player: '玩家说话视频', npc: 'NPC 说话视频' },
        action: { player: '动作视频', npc: '动作视频' },
      },
      marks: {
        submit: '提交',
        input_unlocked: '输入解锁',
        text_kp: 'KP 旁白出现',
        text_npc: 'NPC 文字出现',
        text_npc_action: 'NPC 动作旁白出现',
        text_deferred: '被压住的输出放出',
        options_shown: '建议按钮出现',
        stat_flash: '数值音效与闪光',
        clue_toast: '新线索提示',
        status_kp_done: '状态条：KP 判完',
      },
    },
  },
}
